import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Zap, Gauge } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { lessonUnlocked } from "@/data/curriculum";

const OFFSETS = [0, 20, 40, 60];
const VIDEOS = [
  { key: "Lesson 3", title: "Lesson 3 count-along", lessons: [3] },
  { key: "2/2 and 6/8", title: "Two two and six eight", lessons: [3, 4] },
  ...[4, 5, 6, 7, 8, 9, 10].map((n) => ({ key: `Lesson ${n}`, title: `Lesson ${n} count-along`, lessons: [n] })),
];

const Tick = ({ done, label, testId }) => (
  <span className={`inline-flex items-center gap-1 text-xs ${done ? "font-semibold text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid={testId} data-done={done}>
    {done ? <CheckCircle2 size={14} /> : <Circle size={14} />} {label}
  </span>
);

// Dashboard card: which count-along videos (in lessons the learner owns) are watched and challenged.
export const CountAlongTracker = () => {
  const { purchases } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/countalong").then(({ data: d }) => setData(d)).catch(() => setData({ watched: [], challenges: [], total: 9 })); }, []);
  const owned = VIDEOS.map((v) => ({ ...v, lesson: v.lessons.flatMap((n) => OFFSETS.map((o) => n + o)).find((id) => lessonUnlocked(id, purchases || [])) })).filter((v) => v.lesson);
  if (!data || owned.length === 0) return null;
  const left = owned.filter((v) => !data.watched.includes(v.key) || !data.challenges.includes(v.key)).length;
  return (
    <section className="mt-10 rounded-3xl border border-border bg-card p-6" data-testid="countalong-tracker">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl">Count-along videos</h2>
          <p className="text-sm text-muted-foreground" data-testid="countalong-tracker-left">{left ? `${left} still to go` : "All done. Brilliant counting!"}</p>
        </div>
        <div data-testid="speed-counter-badge" data-won={!!data.speed_counter}
          className={`flex items-center gap-2 rounded-2xl border px-4 py-2 ${data.speed_counter ? "border-transparent bg-primary text-primary-foreground shadow-lg" : "border-dashed border-border opacity-60"}`}>
          <Gauge size={22} />
          <div className="leading-tight">
            <p className="text-sm font-bold">Speed Counter</p>
            <p className="text-[11px]">{data.speed_counter ? "Won!" : `${data.challenges.length}/${data.total} challenges`}</p>
          </div>
        </div>
      </div>
      <ul className="mt-5 divide-y divide-border">
        {owned.map((v, i) => (
          <li key={v.key} className="flex flex-wrap items-center justify-between gap-2 py-3" data-testid={`countalong-row-${i}`}>
            <Link to={`/lessons/${v.lesson}`} className="font-medium hover:text-primary" data-testid={`countalong-link-${i}`}>{v.title}</Link>
            <div className="flex gap-4">
              <Tick done={data.watched.includes(v.key)} label="Watched" testId={`countalong-watched-${i}`} />
              <Tick done={data.challenges.includes(v.key)} label={<><Zap size={11} className="inline" /> Challenge</>} testId={`countalong-challenge-${i}`} />
            </div>
          </li>
        ))}
      </ul>
      {owned.length < data.total && <p className="mt-3 text-xs text-muted-foreground">The Speed Counter badge needs all {data.total} challenges, so unlock the rest of the course to win it.</p>}
    </section>
  );
};
